import type { MeCustomerUser } from 'services/assist-api/entities/me/me';
import type { ROLE } from 'services/assist-api/entities/role/roles';

// A constructed object representing a user (normally the current user) being authorised.
export type AuthorisableUser = {
  // The unique ID on the user entity. Optional since the user data could be loading.
  id?: number;

  // The unique ID on the customerUsers entity. Optional, since the user may not be logged into an
  // organisation at the time that authorisation takes place.
  customerUser?: MeCustomerUser;

  // Roles are required as we are generally always authorising against these. These are the roles
  // used in the context of authorising the user for the current organisation, with system roles
  // from the User entity also included:
  roles: ROLE[];

  // Roles that the user has for the area being authorised only:
  areaLevelRoles?: ROLE[];

  // Optional booleans - needed for specific scenarios/policies but not generally used.
  belongsToCurrentArea?: boolean;
  belongsToMarkingOrganisation?: boolean;
  isMasquerading?: boolean;
};

// Helper functions (don't use these directly for authorising actions; use a policy action):

/**
 * Returns true if and only if the user has at least one of the accepted organisation-level roles.
 */
export function hasRole(user: AuthorisableUser, acceptedRoles: ROLE[]): boolean {
  return user.roles.some((role) => acceptedRoles.includes(role));
}

/**
 * Returns true if and only f the user has at least one of the accepted area-level roles.
 */
export function hasAreaLevelRole(user: AuthorisableUser, acceptedRoles: ROLE[]): boolean {
  return (user.areaLevelRoles || []).some((role) => acceptedRoles.includes(role));
}
